import { Body1, Caption } from '@xo-union/tk-ui-typography';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { Maybe, ThemesQuery } from '../../../generated/graphql';
import TrackingContext, { getThemeBaseProps } from '../../helpers/TrackingContext';
import { NavigationContext } from '../../helpers/NavigationContext';
import { SortOrder } from '../../helpers/useThemes';
import ThemeListImageContainer from '../ThemeListImageContainer';
import styles from './index.scss';
import { useWebsiteLayoutContext } from '../../../context/WebsiteLayoutContext';

interface ThemePhoto {
  mediaApiId: string;
}

interface Designer {
  companyName: Maybe<string>;
  name: Maybe<string>;
}

type FamilyTheme = ThemesQuery['familyThemes'][0];

interface Props {
  familyTheme: FamilyTheme;
  index: number;
  key: Maybe<string>;
  sortOrder?: SortOrder;
}

interface ButtonProps {
  className?: string;
  onClick?: () => void;
}

const Card: React.FC<Props> = ({ familyTheme, index, sortOrder }) => {
  const { track, checkGridImpression, addGridImpression } = useContext(TrackingContext);
  const selectedTheme = familyTheme?.themes && familyTheme?.themes[0];
  const [inViewRef, inView] = useInView({ threshold: 1, triggerOnce: true });
  const trackImpression = useCallback(() => {
    if (selectedTheme?.id && !checkGridImpression(selectedTheme.id)) {
      track('Design Viewed', {
        ...getThemeBaseProps(selectedTheme, index, 0, familyTheme),
        page: 'theme grid',
        viewedByColorwayClick: false,
      });
      addGridImpression(selectedTheme.id);
    }
  }, [track, selectedTheme, index, addGridImpression, checkGridImpression, familyTheme]);
  const thisRef = useRef<null | HTMLDivElement>(null);
  const { scrollDepth, setScrollDetails, screenWidth, cardIndex } = useContext(NavigationContext);
  const { layout } = useWebsiteLayoutContext();

  useEffect(() => {
    if (inView) {
      trackImpression();
    }
  }, [inView, trackImpression]);

  const selectDesign = () => {
    // store location of current card when clicked on
    setScrollDetails(window.pageYOffset, window.outerWidth, index);
    trackDesignSelection();
  };

  const themeBaseProps = getThemeBaseProps(selectedTheme, index, 0, familyTheme);

  const trackDesignSelection = () => {
    const isPinnedTheme = sortOrder === SortOrder.pinned && index < 6;
    track('Design Selection', {
      ...themeBaseProps,
      page: 'wws create theme grid',
      selection: isPinnedTheme ? 'select_pinned' : 'select',
      layout,
    });
  };

  useEffect(() => {
    if (index === cardIndex && scrollDepth > 0) {
      // if stored screen width hasn't changed, scroll to saved depth
      // otherwise scroll this card to view
      if (screenWidth === window.outerWidth) {
        window.scrollTo(0, scrollDepth);
      } else {
        thisRef?.current?.scrollIntoView();
      }
    }
  }, [cardIndex, index, screenWidth, scrollDepth]);
  return (
    <div className={styles.container} ref={inViewRef} data-testid={`theme-card-${selectedTheme?.id}`}>
      {selectedTheme?.previewImage?.mediaApiId && (
        <div className={styles.imageContainer} ref={thisRef}>
          <ThemeListImageContainer
            mediaId={selectedTheme?.previewImage?.mediaApiId}
            mediaUrl={selectedTheme?.previewImage?.mediaUrl}
            mobileMediaId={selectedTheme?.mobilePreviewImage?.mediaApiId}
            mobileMediaUrl={selectedTheme?.mobilePreviewImage?.mediaUrl}
            themeId={selectedTheme.id}
            onClick={selectDesign}
          />
        </div>
      )}
      <Body1 className={styles.themeTitle}>
        {selectedTheme?.name}
        {selectedTheme?.themeDesigner && selectedTheme?.designerUrl && (
          <p className={styles.invitationLink}>
            <Caption href={selectedTheme?.designerUrl} as="a" target="_blank" rel="noopener noreferrer">
              {`See matching invitations`}
            </Caption>
          </p>
        )}
      </Body1>
    </div>
  );
};

export default Card;
